import {
  KS_RAYLEIGH, columnGlow, eyeLimitFromSky, eyeLimitingMagnitude, eyeSkyBrightness, eyeSkyParts, glowAirMass, moonScatter, naturalGlow,
} from '../astro/skybrightness';
import { DEG, clamp, smoothstep, type Vec3 } from '../lib/math';

// Naked eye: the sky as the eye sees it tonight. One test decides what is drawn, named and tapped: a point of light is
// seen when, dimmed by the air at its height, it is no fainter than the eye's limit where it is: the limit overhead,
// less where the sky glows brighter, low down and near the Moon (eyeSeesAt). The star shader draws to the same test
// (stars.ts, u_eye), so every star on show is named and every name has a star. The rules live here, apart from the
// drawing, so they can be checked on their own.

/** Extinction, in magnitudes per air mass: the star shader's u_extK. */
export const EXT_K = 0.25;
/** The faintest the eye's limit goes, however dark the sky and whatever the person's nudge. */
export const EYE_MAX = 7.5;
/** A moving point of light (a satellite) has to be a little brighter than a star to be picked out. */
export const MOVING_MARGIN = 0.3;
/** Satellites lower than this (sine of 10°) are lost in the haze near the horizon, behind buildings and trees. */
export const SAT_MIN_SIN = Math.sin(10 * DEG);

/** Air mass for a true altitude whose sine is `s`: 1 overhead, about 40 on the horizon (the star shader's formula,
 *  Krisciunas & Schaefer 1991 eq. 14 after Rozenberg 1966). */
export function airMass(s: number): number {
  const h = Math.max(s, 0);
  return 1 / (h + 0.025 * Math.exp(-11 * h));
}

/**
 * How bright a point of magnitude `mag` looks at a true altitude whose sine is `s`: fainter low down, where its light
 * crosses more air. Below the horizon it is hidden (Infinity), unless the ground is see-through, when it shows without
 * the air's dimming, as the star shader draws it.
 */
export function seenMag(mag: number, s: number, throughGround = false): number {
  if (s < -0.03) return throughGround ? mag : Infinity;
  return mag + EXT_K * (airMass(s) - 1);
}

/** Whether the eye sees a point of magnitude `mag` at altitude sine `s`, with `limit` the faintest it sees overhead. */
export const eyeSees = (mag: number, s: number, limit: number, throughGround = false) => seenMag(mag, s, throughGround) <= limit;

/**
 * The eye's limit: the faintest star it can see overhead, from the sky's brightness (`sqm`: the darkness check's
 * measurement where there is one, else the light-pollution estimate), twilight and moonlight, moved by what the person
 * says they can see (`nudge`, magnitudes). There is no zoom or display boost: a phone that zooms in does not make the
 * eye see fainter stars, so zooming in spreads the same stars apart (and leaves room to name more of them).
 */
export function eyeLimit(sunAlt: number, moonAlt: number, moonMag: number, bortle: number, sqm: number | null, nudge: number): number {
  return Math.min(EYE_MAX, eyeLimitingMagnitude(sunAlt, moonAlt, moonMag, bortle, sqm) + nudge);
}

// ---------- Across the sky: the glow ----------
/**
 * Tonight's sky for the eye, over the whole sky: the light overhead by its sources (astro/skybrightness.ts,
 * eyeSkyParts), each glowing its own way towards the horizon and around the Moon (the models are cited there), so the
 * eye's limit varies with direction as the eye finds it. Worked out once a frame (the engine's FrameContext.eyeSky).
 */
export interface EyeSky {
  /** The eye's limit overhead, tonight's nudge included, at most EYE_MAX: eyeLimit, Naked eye's limit (nelm). */
  limit: number;
  /** The faintest the limit is anywhere on the sky tonight (or a little fainter): no star fainter than this can show. */
  max: number;
  /** The most the sky's glow anywhere lowers a star's magnitude as if overhead (glowLoss; 0 or less). */
  minLoss: number;
  /** Tonight's nudge (magnitudes). */
  nudge: number;
  /** The sky overhead (mag/arcsec², eyeSkyBrightness) and the eye's limit against it, before the nudge and the cap. */
  sky: number;
  eye: number;
  /** Shares of the light overhead: the natural sky, artificial light, twilight and the Moon (adding up to 1). */
  nat: number;
  art: number;
  tw: number;
  moon: number;
  /** The Moon's direction (east, north, up). */
  mx: number;
  my: number;
  mz: number;
  /** 1 / the Moon's scattering (moonScatter) where its glow is anchored to the overhead model (MOON_ANCHOR). */
  moonRef: number;
}

/**
 * The overhead model's moonlight follows Krisciunas & Schaefer's value overhead to within about 0.15 magnitudes while
 * the Moon is 10° to 60° high, but it leaves out the glare round the Moon. With the Moon higher, overhead is in that
 * glare, so the Moon's glow is anchored to the model 30° from the Moon instead.
 */
const MOON_ANCHOR = 30;

/** The eye's sky (EyeSky) for a Sun `sunAlt` degrees up, the Moon in direction `moonEnu` (east, north, up) with
 *  magnitude `moonMag`, the light pollution (as eyeLimit) and tonight's nudge. */
export function eyeSkyFor(sunAlt: number, moonEnu: Vec3, moonMag: number, bortle: number, sqm: number | null, nudge: number): EyeSky {
  const moonAlt = Math.asin(clamp(moonEnu[2], -1, 1)) / DEG;
  const eye = eyeLimitingMagnitude(sunAlt, moonAlt, moonMag, bortle, sqm);
  const sky = eyeSkyBrightness(sunAlt, moonAlt, moonMag, bortle, sqm);
  const p = eyeSkyParts(sunAlt, moonAlt, moonMag, bortle, sqm);
  const sum = p.nat + p.art + p.tw + p.moon;
  const moonRef = 1 / moonScatter(Math.max(90 - moonAlt, MOON_ANCHOR));
  // The darkest the sky can be anywhere: the natural sky, artificial light and twilight no darker than overhead, and
  // the Moon's scattering no less than the air's own (Rayleigh) part at its least, 90° from the Moon.
  const darkest = (p.nat + p.art + p.tw) / sum + (p.moon / sum) * KS_RAYLEIGH * 1.06 * moonRef;
  const most = eyeLimitFromSky(sky - 2.5 * Math.log10(darkest));
  return {
    limit: Math.min(EYE_MAX, eye + nudge), max: Math.min(EYE_MAX, most + nudge), minLoss: eye - most, nudge, sky, eye,
    nat: p.nat / sum, art: p.art / sum, tw: p.tw / sum, moon: p.moon / sum, mx: moonEnu[0], my: moonEnu[1], mz: moonEnu[2], moonRef,
  };
}

/** The sky's light in direction (x, y, z) (east, north, up: a unit vector) as a share of the light overhead. */
function glowShare(s: EyeSky, x: number, y: number, z: number): number {
  const X = glowAirMass(z > 0 ? z : 0);
  const column = columnGlow(X, EXT_K);
  let share = s.nat * naturalGlow(X, EXT_K) + s.art * column + s.tw;
  if (s.moon > 0) share += s.moon * moonScatter(Math.acos(clamp(x * s.mx + y * s.my + z * s.mz, -1, 1)) / DEG) * s.moonRef * column;
  return share;
}
/** The eye's limit in direction (x, y, z) against the sky's glow there, before the nudge and the cap. */
const limitThere = (s: EyeSky, x: number, y: number, z: number) => eyeLimitFromSky(s.sky - 2.5 * Math.log10(glowShare(s, x, y, z)));

/**
 * How much brighter than overhead a star must be in direction (x, y, z) for the eye to see it against the sky's glow
 * there (magnitudes): more low down and near the Moon, and less than nothing in a part of the sky darker than overhead
 * (well away from a bright Moon).
 */
export const glowLoss = (s: EyeSky, x: number, y: number, z: number) => s.eye - limitThere(s, x, y, z);

/** The eye's limit in direction (x, y, z) tonight: the faintest star it sees there before the air dims it (seenMag),
 *  with tonight's nudge, at most EYE_MAX. Overhead it is the limit (EyeSky.limit). */
export const eyeLimitAt = (s: EyeSky, x: number, y: number, z: number) => Math.min(EYE_MAX, limitThere(s, x, y, z) + s.nudge);

/**
 * Naked eye's test: whether the eye sees a point of magnitude `mag` in direction (x, y, z) tonight, dimmed by the air
 * at its height and against the sky's glow there. Below the horizon, through a see-through ground, it shows as it would
 * overhead, as the star shader draws it.
 */
export function eyeSeesAt(mag: number, x: number, y: number, z: number, s: EyeSky, throughGround = false): boolean {
  const m = seenMag(mag, z, throughGround);
  if (m > s.max) return false;
  return m <= (z < -0.03 ? s.limit : eyeLimitAt(s, x, y, z));
}

/** As the eye sees a point of magnitude `mag` in direction (x, y, z) (the air and the glow there): its magnitude as if
 *  it were overhead, which the limit overhead decides (the darkness check's and the cards' terms). */
export function eyeMagAt(mag: number, x: number, y: number, z: number, s: EyeSky, throughGround = false): number {
  const m = seenMag(mag, z, throughGround);
  return z < -0.03 ? m : m + glowLoss(s, x, y, z);
}

/** The catalogue's magnitudes counted once: how many stars are no fainter than −2, −1.95, −1.9 … 8. */
const tallies = new WeakMap<object, Float64Array>();
function tally(mags: ArrayLike<number>): Float64Array {
  let t = tallies.get(mags);
  if (!t) {
    t = new Float64Array(201);
    for (let i = 0; i < mags.length; i++) {
      const k = Math.ceil((mags[i] + 2) * 20);
      if (k < t.length) t[Math.max(0, k)]++;
    }
    for (let k = 1; k < t.length; k++) t[k] += t[k - 1];
    tallies.set(mags, t);
  }
  return t;
}
/** How many of the catalogue's stars are no fainter than `m` (smoothly, between its steps). */
function brighterThan(t: Float64Array, m: number): number {
  const u = (m + 2) * 20;
  if (u <= 0) return 0;
  if (u >= t.length - 1) return t[t.length - 1];
  const k = Math.floor(u);
  return t[k] + (t[k + 1] - t[k]) * (u - k);
}

/**
 * About how many stars the eye picks out at once above the horizon under sky `s`, from a catalogue's magnitudes `mags`:
 * Naked eye's own test (the limit where each part of the sky is, less the air there), over equal areas of the sky,
 * counting the stars as if spread evenly (Where to go).
 */
export function starsSeen(s: EyeSky, mags: ArrayLike<number>): number {
  const t = tally(mags);
  // Bands of equal area, by the sine of the height; round the sky only where the Moon makes it uneven, and then just
  // one side of the Moon, as the other is its mirror. Within 1% of a grid a hundred times finer.
  const bands = 16, round = s.moon > 0 ? 6 : 1;
  const az0 = Math.atan2(s.mx, s.my);
  let n = 0;
  for (let b = 0; b < bands; b++) {
    const z = (b + 0.5) / bands, r = Math.sqrt(1 - z * z), air = seenMag(0, z);
    for (let a = 0; a < round; a++) {
      const az = az0 + (round > 1 ? ((a + 0.5) / round) * Math.PI : 0);
      n += brighterThan(t, eyeLimitAt(s, r * Math.sin(az), r * Math.cos(az), z) - air);
    }
  }
  return n / (2 * bands * round);
}

/** The eye's sky on a clear night with no Moon, the Sun well down: what Settings and the Dark skies lesson count. */
export const moonlessSky = (bortle: number, sqm: number | null, nudge: number) => eyeSkyFor(-30, [0, 0, -1], -12.7, bortle, sqm, nudge);
/** A count of stars as Settings and the lesson both say it: to the nearest 100 from 1,000, else the nearest 10. */
export const roundStars = (n: number) => (n >= 1000 ? Math.round(n / 100) * 100 : Math.max(10, Math.round(n / 10) * 10));

/**
 * How much of the Milky Way the eye makes out with this limit: none in a town sky (Bortle class 7, about 4.7 in the
 * limit model, where it "is nearly or totally invisible"), a little in class 6 (5.2, "only visible near the zenith")
 * and all of it from about 5.9 (between classes 5 and 4).
 */
export const milkyWaySeen = (limit: number) => smoothstep(4.9, 5.9, limit);

/**
 * The deep-sky objects an eye can pick out, each with the limit (overhead) it needs: it shows once the eye's limit
 * reaches that, dimmed by the air like a star. Set from the Bortle scale's descriptions (Bortle 2001, Sky & Telescope)
 * through the limit model (class 9 about 3.8, 8: 4.3, 7: 4.7, 6: 5.2, 5: 5.8, 4: 6.2, 3: 6.4): in class 9 "aside from
 * the Pleiades, no Messier object is visible"; Andromeda and the Beehive "may be glimpsed" in class 7 and Andromeda is
 * "modestly apparent" in class 6; Triangulum is "a difficult averted vision object" in class 4 and easy in class 3,
 * where M4, M5, M15 and M22 are naked-eye objects too. The others are estimates placed between these by brightness and
 * size. Only the classic naked-eye objects are listed: a catalogue magnitude spread over a wide patch, or a nebula's
 * that is really its star's, would put on the sky things nobody can see.
 */
export const DSO_EYE: Readonly<Record<string, number>> = {
  // Seen from towns and cities.
  M45: 3.6, // Pleiades
  'IC 2391': 4.0,
  'NGC 5139': 4.4, // Omega Centauri
  M42: 4.6, // Orion Nebula: the sword's misty middle star
  'NGC 104': 4.6, // 47 Tucanae
  'NGC 3372': 4.6, // Carina Nebula
  'NGC 3532': 4.6,
  'ESO056-115': 4.6, // Large Magellanic Cloud
  M7: 4.8,
  'NGC 6231': 4.8,
  M31: 4.9, // Andromeda Galaxy
  M44: 4.9, // Beehive
  // Suburban skies.
  'NGC 869': 5.0, // h Persei, with
  'NGC 884': 5.0, // chi Persei: the Double Cluster
  'NGC 2516': 5.0,
  'NGC 292': 5.2, // Small Magellanic Cloud
  Cl399: 5.3, // Brocchi's Cluster, the Coathanger
  M6: 5.4,
  M41: 5.5,
  M47: 5.5,
  'IC 4665': 5.5,
  M39: 5.6,
  M24: 5.6, // the Sagittarius star cloud
  M22: 5.7,
  'IC 4756': 5.8,
  // Rural and dark skies.
  M8: 5.9, // Lagoon Nebula
  M25: 5.9,
  M35: 5.9,
  M34: 6.0,
  'NGC 6752': 6.0,
  M11: 6.1,
  M13: 6.1, // Hercules Globular Cluster
  M37: 6.2,
  M48: 6.2,
  'NGC 752': 6.2,
  'NGC 6397': 6.2,
  M4: 6.3,
  M5: 6.3,
  M33: 6.3, // Triangulum Galaxy
  M3: 6.4,
  M15: 6.4,
  M36: 6.4,
  M50: 6.4,
  M2: 6.5,
  M38: 6.5,
  M92: 6.5,
};

/**
 * How many star names Naked eye shows at once: one for every 5,000 square points of the screen that names can use
 * (about 50 on a phone held upright, 40 on its side), fewer when the reader's text is larger. Brightest first, so a
 * crowded dark sky shows its brightest stars' names, and zooming in, which spreads the stars apart, names fainter ones.
 * What is chosen, and what is in the pointing circle, are named on top of these.
 */
export function nameBudget(width: number, height: number, textScale = 1): number {
  return Math.max(12, Math.round((Math.max(width, 0) * Math.max(height, 0)) / (5000 * textScale * textScale)));
}

/**
 * Stars within `sepDeg` (3′) of a brighter one: the parts of a close double, listed separately in the catalogue (Capella,
 * Mizar, Castor). To the eye each pair is one point of light, named once, for its brighter part. Flags for every star
 * down to `maxMag` (the catalogue is sorted brightest first), found through a grid of cells `sepDeg` across.
 */
export function closeCompanions(dir: ArrayLike<number>, mag: ArrayLike<number>, count: number, maxMag = EYE_MAX, sepDeg = 0.05): Uint8Array {
  const out = new Uint8Array(count);
  const cell = sepDeg * DEG, cos = Math.cos(cell);
  const grid = new Map<number, number[]>();
  const key = (a: number, b: number, c: number) => ((a * 73856093) ^ (b * 19349663) ^ (c * 83492791)) | 0;
  for (let i = 0; i < count && mag[i] <= maxMag; i++) {
    const x = dir[i * 3], y = dir[i * 3 + 1], z = dir[i * 3 + 2];
    const a = Math.floor(x / cell), b = Math.floor(y / cell), c = Math.floor(z / cell);
    search: for (let da = -1; da <= 1; da++) {
      for (let db = -1; db <= 1; db++) {
        for (let dc = -1; dc <= 1; dc++) {
          const near = grid.get(key(a + da, b + db, c + dc));
          if (!near) continue;
          for (const j of near) {
            if (dir[j * 3] * x + dir[j * 3 + 1] * y + dir[j * 3 + 2] * z > cos) {
              out[i] = 1;
              break search;
            }
          }
        }
      }
    }
    const k = key(a, b, c);
    const list = grid.get(k);
    if (list) list.push(i);
    else grid.set(k, [i]);
  }
  return out;
}
